import React, { useState, useEffect } from 'react'
import { useApolloClient } from '@apollo/client'
import { ReceiptService, ReceiptType } from '../lib/receiptService'
import { ReceiptUtils } from '../lib/receiptUtils'
import { Receipt } from '../types/order'
import { XMarkIcon, PrinterIcon, ShareIcon, EyeIcon } from '@heroicons/react/24/outline'

interface ReceiptModalProps {
  isOpen: boolean
  onClose: () => void
  orderId?: number
  paymentId?: number
  receiptType?: ReceiptType
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({
  isOpen,
  onClose,
  orderId,
  paymentId,
  receiptType = 'CUSTOMER'
}) => {
  const apolloClient = useApolloClient()
  const receiptService = new ReceiptService(apolloClient)
  
  const [receiptData, setReceiptData] = useState<Receipt | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    if (!orderId && !paymentId) {
      setError('No order or payment specified for receipt')
      return
    }

    if (orderId || paymentId) {
      loadReceiptData()
    }
  }, [isOpen, orderId, paymentId, receiptType])

  const loadReceiptData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // For refunds (paymentId with refund payment), always use CUSTOMER type
      // The backend will reject MERCHANT for refund payments
      const effectiveReceiptType = receiptType
      
      const data = paymentId
        ? await receiptService.getReceiptByPayment(paymentId, effectiveReceiptType)
        : await receiptService.getReceipt(orderId as number, effectiveReceiptType)
      
      // Safety check: if we got a refund receipt but requested MERCHANT, show error
      if (data.receipt_id?.startsWith('RFD-') && receiptType === 'MERCHANT') {
        setError('Merchant receipts are not available for refund payments')
        return
      }
      
      setReceiptData(data)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load receipt data')
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = () => {
    if (!receiptData) return

    // Refund receipts always use the dedicated refund endpoint (refunds don't support merchant receipts)
    if (receiptData.receipt_id?.startsWith('RFD-')) {
      ReceiptUtils.printRefundReceipt(receiptData)
      return
    }

    // Normal (non-refund) receipts
    if (receiptType === 'MERCHANT') {
      ReceiptUtils.printMerchantReceipt(receiptData)
    } else {
      ReceiptUtils.printReceipt(receiptData)
    }
  }

  const handleView = () => {
    if (!receiptData) return

    // Refund receipts always use customer receipt view (refunds don't support merchant receipts)
    if (receiptData.receipt_id?.startsWith('RFD-')) {
      ReceiptUtils.viewReceipt(receiptData)
      return
    }

    // Normal (non-refund) receipts
    if (receiptType === 'MERCHANT') {
      ReceiptUtils.viewMerchantReceipt(receiptData)
    } else {
      ReceiptUtils.viewReceipt(receiptData)
    }
  }

  const handleShare = async () => {
    if (receiptData) {
      const success = await ReceiptUtils.shareReceipt(receiptData)
      if (success) {
        alert('Receipt URL copied to clipboard!')
      }
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {receiptType === 'MERCHANT' ? 'Merchant Receipt' : 'Customer Receipt'}
            </h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <button
                  onClick={handlePrint}
                  disabled={!receiptData}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  title="Print Receipt"
                >
                  <PrinterIcon className="h-4 w-4" />
                  <span>Print</span>
                </button>
                
                <button
                  onClick={handleView}
                  disabled={!receiptData}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  title="View Receipt"
                >
                  <EyeIcon className="h-4 w-4" />
                  <span>View</span>
                </button>
                
                <button
                  onClick={handleShare}
                  disabled={!receiptData}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  title="Share Receipt"
                >
                  <ShareIcon className="h-4 w-4" />
                  <span>Share</span>
                </button>
              </div>
              
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading receipt...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-600 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Receipt</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={loadReceiptData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : receiptData ? (
              <div className="space-y-6">
                {/* Receipt Header */}
                <div className="text-center border-b border-gray-200 pb-4">
                  <h1 className="text-2xl font-bold text-gray-900">Curry Me</h1>
                  <p className="text-gray-600">Restaurant Receipt</p>
                  <div className="mt-2 text-sm text-gray-500">
                    <p>Order #{receiptData.order_number}</p>
                    {/* order_date arrives ALREADY formatted by the API, in the restaurant
                        timezone ("10-08-2026 14:30", nl-NL dd-mm-yyyy). Do not wrap it in
                        new Date(): JS reads that as mm-dd-yyyy, so 10 Aug silently became
                        8 Oct, and any day past the 12th became "Invalid Date". Pass the
                        string straight through — formatDate returns strings unchanged. */}
                    <p>{ReceiptUtils.formatDate(receiptData.order_date)}</p>
                  </div>
                </div>

                {/* Customer Info */}
                {(receiptData.customer_name || receiptData.staff_name) && (
                  <div className="border-b border-gray-200 pb-4">
                    {receiptData.customer_name && (
                      <p className="text-sm">
                        <span className="font-medium">Customer:</span> {receiptData.customer_name}
                      </p>
                    )}
                    <p className="text-sm">
                      <span className="font-medium">Staff:</span> {receiptData.staff_name}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Payment:</span> {receiptData.payment_method}
                    </p>
                    {receiptData.transaction_id && (
                      <p className="text-sm">
                        <span className="font-medium">Transaction ID:</span> {receiptData.transaction_id}
                      </p>
                    )}
                  </div>
                )}

                {/* Items */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Items</h3>
                  <div className="space-y-2">
                    {receiptData.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-gray-600">
                            {item.quantity}x {ReceiptUtils.formatPrice(item.unit_price)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">{ReceiptUtils.formatPrice(item.total_price)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals — GROSS, mirroring the printed bon (API gross contract,
                    2026-08-10): every amount is what the customer paid, and the only
                    additive identity is Subtotal + Statiegeld + Delivery fee == Total. tax_amount
                    is the VAT already inside that total ("waarvan BTW"), so it renders
                    BELOW Total as a disclosure note, never as a sum line — stacking it
                    above Total makes the visible lines sum past the total. On refund
                    receipts subtotal == total and the fee is absent; same layout holds. */}
                <div className="border-t-2 border-gray-300 pt-4">
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{ReceiptUtils.formatPrice(receiptData.subtotal)}</span>
                    </div>
                    {/* First-party delivery fee, GROSS — the price quoted at checkout,
                        same figure as the printed "Bezorgkosten" line. Rendered only
                        when present: the API omits it entirely on non-delivery orders
                        rather than sending zero, so a pickup receipt keeps its shape. */}
                    {/* Statiegeld, GROSS — carries no BTW, so it adds to Total but
                        nothing to the "waarvan BTW" disclosure below. Absent (not
                        zero) when the order had no deposit-bearing items. */}
                    {receiptData.statiegeld != null && (
                      <div className="flex justify-between text-sm">
                        <span>Statiegeld:</span>
                        <span>{ReceiptUtils.formatPrice(receiptData.statiegeld)}</span>
                      </div>
                    )}
                    {/* Verpakkingskosten, GROSS. Unlike statiegeld this IS
                        taxed, so its VAT is already inside the "waarvan BTW"
                        disclosure below rather than absent from it. */}
                    {receiptData.verpakkingskosten != null && (
                      <div className="flex justify-between text-sm">
                        <span>Verpakkingskosten:</span>
                        <span>{ReceiptUtils.formatPrice(receiptData.verpakkingskosten)}</span>
                      </div>
                    )}
                    {receiptData.delivery_fee != null && (
                      <div className="flex justify-between text-sm">
                        <span>Delivery fee:</span>
                        <span>{ReceiptUtils.formatPrice(receiptData.delivery_fee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span>{ReceiptUtils.formatPrice(receiptData.total)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>waarvan BTW:</span>
                      <span>{ReceiptUtils.formatPrice(receiptData.tax_amount)}</span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="text-center text-xs text-gray-500 pt-4 border-t border-gray-200">
                  <p>Thank you for your business!</p>
                  <p>Generated on {ReceiptUtils.formatDate(new Date())}</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}


export default ReceiptModal

